// src/hooks/useWebSocketHeartbeat.ts
import { useEffect, useRef, useCallback } from 'react';
import wsSingleton from './utils/wsSingleton';

/**
 * 生成 WebSocket URL
 * - 开发环境：使用代理到 localhost:5001
 * - 生产环境：使用相对路径（由 IIS 反向代理）
 */
const generateWebSocketUrl = () => {
  if (typeof window === 'undefined') return '';
  
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  
  // 这里不要写 window.location.host，用相对路径
  return `${protocol}//${window.location.hostname}:${window.location.port}/api/ws`;
};


export const useWebSocketHeartbeat = (userId?: string) => {
  const heartbeatRef = useRef<number | null>(null);
  const wsUrlRef = useRef(generateWebSocketUrl());

  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    // 每次执行时重新生成 URL（支持环境变化）
    wsUrlRef.current = generateWebSocketUrl();
    const wsUrl = wsUrlRef.current;
    
    console.debug('[useWebSocketHeartbeat] WebSocket URL:', wsUrl);

    wsSingleton.init(wsUrl);

    const sendHeartbeat = () => {
      try {
        const payload: any = {
          type: 'heartbeat',
          timestamp: new Date().toISOString(),
        };
        if (userId) payload.userId = userId;
        wsSingleton.send(payload);
        
        console.debug('[useWebSocketHeartbeat] Heartbeat sent');
      } catch (e) {
        console.warn('[useWebSocketHeartbeat] sendHeartbeat error', e);
      }
    };

    const onOpen = () => {
      sendHeartbeat();
      if (heartbeatRef.current) {
        window.clearInterval(heartbeatRef.current);
        heartbeatRef.current = null;
      }
      heartbeatRef.current = window.setInterval(() => {
        sendHeartbeat();
      }, 25000); // 25秒心跳间隔
      
      console.debug('[useWebSocketHeartbeat] onOpen -> heartbeat started');
    };

    const onClose = () => {
      if (heartbeatRef.current) {
        window.clearInterval(heartbeatRef.current);
        heartbeatRef.current = null;
      }
      console.debug('[useWebSocketHeartbeat] onClose -> heartbeat cleared');
    };

    wsSingleton.on('__internal_open', onOpen);
    wsSingleton.on('__internal_close', onClose);

    return () => {
      if (heartbeatRef.current) {
        window.clearInterval(heartbeatRef.current);
        heartbeatRef.current = null;
      }
      wsSingleton.off('__internal_open', onOpen);
      wsSingleton.off('__internal_close', onClose);
    };
  }, [userId]);

  const reconnect = useCallback(() => {
    try {
      wsSingleton.close();
      if (typeof window === 'undefined') return;
      
      const wsUrl = generateWebSocketUrl();
      wsSingleton.init(wsUrl);
      
      console.debug('[useWebSocketHeartbeat] manual reconnect triggered');
    } catch (e) {
      console.error('[useWebSocketHeartbeat] reconnect error', e);
    }
  }, []);

  return { reconnect };
};

export default useWebSocketHeartbeat;